import React, { Fragment, useEffect, useMemo } from 'react';
import Button from 'b2b-ui-components-lib/lib/components/button';
import { useHistory } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import {
  getProductType,
  isNLD_OR_LL,
  PRODUCT_TYPE,
  isBulkUpgrade,
  TYPEOF_ORDER,
  isShiftingJourney,
  isPreOrder,
  isAOIOrder,
  isAOILandingPage,
  ROUTES
} from 'common/constants/app.constants';
import { ClickEventType } from 'home/view/check-feasibility/comp-generator/common-interface';
import { AOI_TMO_REDIRECTED, MULTI_SITE_MODULE_ID, SINGLE_SITE } from 'home/view/manage-feasibility/constants';
import downloadIcon from 'assests/download.svg';
import { LEAD_STATUS_MSG, MODULE_ID, SELECTED_PARTY_DATA } from 'home/common/constants/constants';
import { CUSTOMER_MESSAGE, isUserTypeIsKAM } from 'dedicated/components/plan-and-configuration/configure-plan-pricing-constant';
import { getCurrentLead, getCurrentOrder } from 'tokenGeneration/lead.util';
import { getFromSessionStorage, findParamFromUrl, isSingleUpgradeOrShiftingJourney } from 'common/utils/app.utils';
import { fetchOrder } from 'common/services/http.service';
// import { SESSION_STORAGE } from 'home/view/upgrade-order/constants';

export type ProductType = {
  VPN: string;
  DEDICATED_INTERNET: string;
};

export const getDownloadText = () => {
  const DEDICATED_TEXT = 'Add multiple locations details like company name, address and pincode and then upload the file here.';
  const NLD_TEXT = (
    <span>
      Add installation addresses point A and point B along with required bandwidths and then upload the file here to view feasibility status.
      <p className="download_info_text">Please note that the distance between installation address point A and B should be equal/more than 45 kms.</p>
    </span>
  );

  const LL_TEXT = (
    <span>
      Add installation addresses point A and point B along with required bandwidths and then upload the file here to view feasibility status.
      <p className="download_info_text">Please note that the distance between installation address point A and B should be less than 45 kms.</p>
    </span>
  );

  const NLD_LL_TEXT = getProductType() === PRODUCT_TYPE.NLD ? NLD_TEXT : LL_TEXT;

  const BULK_UPGRADE_TEXT = `Upload circuits to be upgraded by editing the downloaded circuit inventory using “Download Circuits”.`;

  if (isNLD_OR_LL()) {
    return NLD_LL_TEXT;
  }

  if (isBulkUpgrade()) {
    return BULK_UPGRADE_TEXT;
  }

  return DEDICATED_TEXT;
};

export const getSampleFileName = () => {
  if (isBulkUpgrade()) {
    return 'Download Circuits';
  }

  return 'Download Sample.xlsx';
};

export const getDownloadModalText = (isPlanConfigStage = false) => {
  if (isBulkUpgrade()) {
    return 'Download the active sites, add your requirement against each site, shortlist it offline and upload the file here.';
  }

  return (
    <div className="download-modal-helper-text">
      Download the sites to do the following actions on the excel file:
      <ul>
        {isPlanConfigStage && (
          <li>
            View pricing of all feasible sites. Please note charges in case of additional CAPEX and/or third party charges will be updated by the key account manager, if
            applicable.
          </li>
        )}
        <li>Delete sites that are not required for ordering.</li>
        <li>Save the file with the changes and upload it here to proceed with the ordering process.</li>
      </ul>
    </div>
  );
};

export const getDownloadModalTextForSummary = () => {
  return isAOIOrder() ? (
    <div className="download-modal-helper-text">
      Download the sites to do the following actions on the excel file:
      <ul>
        <li>Please note that no price related update is possible at this stage. Request a new order, if required a different quotation.</li>
        <li>Add GST Number, Site contact person details for all sites. These are mandatory to be filled to complete the order.</li>
        <li>Save the file with the changes and upload it here to proceed with the ordering process.</li>
      </ul>
    </div>
  ) : (
    <div className="download-modal-helper-text">
      Download the sites to do the following actions on the excel file:
      <ul>
        <li>Please note that no price related update is possible at this stage. Request a new order, if required a different quotation.</li>
        <li>
          Add GST Number, Billing address, Shipping address, Billing contact person details, Site contact person details for all sites. These are mandatory to be filled to complete
          the order.
        </li>
        <li>Save the file with the changes and upload it here to proceed with the ordering process.</li>
      </ul>
    </div>
  );
};

export const getDownloadModalTextForUpgrade = () => {
  return (
    <div className="download-modal-helper-text">
      Download the sites to do the following actions on the excel file:
      <ul>
        <li>Please note that no price related update is possible at this stage. Request a new order, if required a different quotation.</li>
        <li>The site contact details are filled same as the order contact details. If required, please change for the respective sites in the excel sheet.</li>
        <li>Save the file with the changes and upload it here to proceed with the ordering process.</li>
      </ul>
    </div>
  );
};
export const getUploadModalText = () => {
  return (
    <div className="download-modal-helper-text">
      Upload the excel file of shortlisted sites for an upgrade request here. <br />
      Please note the following details while editing the excel document {''}:
      <ul>
        <li>Deleted sites from the excel cannot be re-uploaded on the system for this upgrade request. </li>
        <li>A new upgrade request has to be created to add new sites.</li>
      </ul>
    </div>
  );
};

export const getNotificationTextOnCircuitUpload = ({
  resolvedCircuitElements,
  totalAddressesCount,
  invalidElements,
  productType,
  handleGotoCheckFeasibility,
  download,
  message
}: {
  resolvedCircuitElements: number;
  totalAddressesCount: number;
  invalidElements: number;
  productType: string;
  handleGotoCheckFeasibility: () => void;
  download: () => void;
  message: string;
}) => {
  const TOAST_PRODUCT_MAP = {
    VPN: 'VPN (MPLS)',
    DEDICATED_INTERNET: 'Dedicated Internet (ILL)'
  };

  if (invalidElements > 0) {
    return (
      <div>
        <b className="bold-text">NOTE!</b>
        {message}{' '}
        <b className="bold-text text-underline" role="button" onClick={() => download()}>
          Download Error Report.
        </b>{' '}
      </div>
    );
  }

  return isBulkUpgrade() ? (
    <div>
      <b className="bold-text">SUCCESS!</b> All{' '}
      <b className="bold-text">
        {resolvedCircuitElements} {resolvedCircuitElements > 1 ? 'circuits' : 'circuit'}
      </b>{' '}
      {resolvedCircuitElements > 1 ? 'are' : 'is'} uploaded on the system for ‘{TOAST_PRODUCT_MAP[productType as keyof ProductType]}’ upgrade request.{' '}
      <b className="bold-text text-underline" role="button" onClick={() => handleGotoCheckFeasibility()}>
        Proceed with Feasibility Check
      </b>
    </div>
  ) : (
    <div>
      <b className="bold-text">{resolvedCircuitElements}</b> out of <b className="bold-text">{totalAddressesCount}</b> {totalAddressesCount > 1 ? 'addresses have' : 'address has'}{' '}
      been verified successfully.{' '}
      <b className="bold-text text-underline" role="button" onClick={() => handleGotoCheckFeasibility()}>
        Proceed with Feasibility Check
      </b>
    </div>
  );
};

export const getFeasibilityWarningText = ({ feasibilityStatusMap, totalElement }: { feasibilityStatusMap: any; totalElement: number }) => {
  if (isBulkUpgrade()) {
    const isAllManual = feasibilityStatusMap['MANUAL_INTERVENTION_REQUIRED'] === totalElement;

    const toastMsg = isAllManual
      ? ` All sites processed, ${feasibilityStatusMap['MANUAL_INTERVENTION_REQUIRED']} ${
          feasibilityStatusMap['MANUAL_INTERVENTION_REQUIRED'] === 1 ? 'site' : 'sites'
        } require manual network check. Our team will contact you. `
      : ` All sites processed, ${feasibilityStatusMap['MANUAL_INTERVENTION_REQUIRED']} ${
          feasibilityStatusMap['MANUAL_INTERVENTION_REQUIRED'] === 1 ? 'site' : 'sites'
        } require manual network check. ${
          feasibilityStatusMap['FEASIBLE'] > 0
            ? `You can proceed with ${feasibilityStatusMap['FEASIBLE']} feasible ${feasibilityStatusMap['FEASIBLE'] === 1 ? 'site' : 'sites'}.`
            : ''
        } Our team will contact you. `;

    return toastMsg;
  }

  return `All Sites Processed, Manual Network Check Required. There are ${feasibilityStatusMap['MANUAL_INTERVENTION_REQUIRED']} ${
    isNLD_OR_LL() ? 'link' : 'sites'
  } that needs a manual network check. Our team will contact you within 24hrs for the network check. You may proceed ahead with the order for feasible sites.`;
};

const getMultiSiteSubText = (onDownloadTemplateClick = (e: ClickEventType) => {}): React.ReactNode => {
  if (isBulkUpgrade()) {
    return (
      <Fragment>
        <span className="sub-heading">Upload .xls file with complete circuit details that need to be upgraded.</span>
        <span className="sub-heading">
          You can download new template or{' '}
          <a role="button" onClick={onDownloadTemplateClick}>
            Download Existing Circuits
          </a>{' '}
          and upload up to 500 circuits for upgrade.
        </span>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <span className="sub-heading">Upload .xls file with complete site details to get the accurate network feasibility report.</span>
      <span className="sub-heading">You can download the template according to your product selection above and upload up to 500 sites.</span>
    </Fragment>
  );
};

export const getInstallationDetailsAccordionHeading = (onlyHeading = false, siteType = { id: '' }, onDownloadTemplateClick = (e: ClickEventType) => {}): React.ReactNode => {
  const singleSite = siteType?.id === SINGLE_SITE;

  if (onlyHeading) {
    return <h2 className="heading">Installation Details</h2>;
  }

  if (singleSite) {
    return (
      <Fragment>
        <h2 className="heading">Installation Details</h2>
        <span className="sub-heading">Please provide the site details to get accurate network feasibility report.</span>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <h2 className="heading">Installation Details</h2>
      {getMultiSiteSubText(onDownloadTemplateClick)}
    </Fragment>
  );
};

export const getInstallationDetailsAccordionHeadingAOI = (onlyHeading = false, siteType = { id: '' }, onDownloadTemplateClick = (e: ClickEventType) => {}): React.ReactNode => {
  return (
    <Fragment>
      <h2 className="heading">Installation Details</h2>
    </Fragment>
  );
};

export const getFileFormatErrorTemplate = (file: any, handleDownloadErrorReportClick: () => void): React.ReactNode | undefined => {
  const onDownloadClick = (e: ClickEventType) => {
    e.preventDefault();
    handleDownloadErrorReportClick();
  };

  return (
    <Fragment>
      <div className="text-msg">There are formatting errors in your file {file?.name}. Please check error report and upload again.</div>

      <div className="error-download-section">
        <img src={downloadIcon} className="icon" />
        <div className="link-button" role="button" onClick={onDownloadClick}>
          Download Error Report
        </div>
      </div>
    </Fragment>
  );
};

export const getAuthHeaderText = (): React.ReactNode => {
  return (
    <div className="auth-header-content">
      <span className="title_text_buy">Buy Airtel Services</span>
      <span className="sub_title_text">Enter a few details to check serviceability & place order</span>
    </div>
  );
};

export const getHeaderInfo = (orderDetail: any) =>
  useMemo((): React.ReactNode => {
    const history = useHistory();
    const trackMyOrderButtonHandler = () => {
      history.push(ROUTES.AOI.TRACK_ORDER_AOI);
    };

    // const activeOrder = getFromSessionStorage(SESSION_STORAGE.MODULE_ID, SESSION_STORAGE.ORDER_DETAILS);
    // const currentOrderType = (getCurrentLead()?.orderType || activeOrder?.orderType) === TYPEOF_ORDER.NEW ? 'New' : 'Upgrade';
    // let currentProductType = getCurrentLead()?.serviceType || getProductType() || activeOrder?.lead?.serviceType;
    let currentOrderType = (getCurrentLead()?.orderType || orderDetail?.orderType) === TYPEOF_ORDER.NEW ? 'New' : orderDetail?.orderSubType === 'SHIFTING' ? 'Shifting' : 'Upgrade';
    let currentProductType = getCurrentLead()?.serviceType || getProductType() || orderDetail?.lead?.serviceType;
    let t = getCurrentLead();
    let isAOITmoRedirected = getFromSessionStorage(MULTI_SITE_MODULE_ID, AOI_TMO_REDIRECTED) || false;
    // const productTypeAlias = currentProductType === 'DEDICATED_INTERNET' ? 'ILL' : currentProductType;
    // if(currentProductType === 'DSL')
    //   {
    //     currentOrderType = 'New';
    //     // isAOIOrder
    //   }
    if (isPreOrder()) {
      currentOrderType = 'New Pre';
      if (isBulkUpgrade()) {
        currentOrderType = 'Upgrade Pre';
      }
    }

    if (isSingleUpgradeOrShiftingJourney()) {
      // currentProductType = getCurrentLead()?.lineItems?.[0]?.productName || activeOrder?.lead?.serviceType;
      currentProductType = getCurrentLead()?.lineItems?.[0]?.productName || orderDetail?.lead?.serviceType;
    }

    const currentAccountData = getFromSessionStorage(MODULE_ID, SELECTED_PARTY_DATA);
    const currentCustomerParty = getCurrentLead()?.customer?.authorizedSignatory?.companyName || orderDetail?.lead?.customer?.authorizedSignatory?.companyName;
    // const currentCustomerParty = getCurrentLead()?.customer?.authorizedSignatory?.companyName ?? activeOrder?.lead?.customer?.authorizedSignatory?.companyName;
    const currentCustomerPartyForSingleUpgrade = getCurrentLead()?.customer?.name;
    const singleUpgradeLeadId = getCurrentLead()?.leadId ?? '';

    const getTitleName = () => {
      if (isShiftingJourney()) {
        return 'Shifting Order Request';
      }

      return 'Upgrade Order Request';
    };

    const getAccountName = () => {
      if (isUserTypeIsKAM() || isSingleUpgradeOrShiftingJourney()) {
        return getCurrentLead()?.customer?.name || orderDetail?.lead?.customer?.authorizedSignatory?.companyName;
        // return getCurrentLead()?.customer?.name || activeOrder?.lead?.customer?.authorizedSignatory?.companyName;
      }

      return currentAccountData?.partyName || currentCustomerParty || currentCustomerPartyForSingleUpgrade;
    };

    const getProductName = () => {
      if (isUserTypeIsKAM() || isSingleUpgradeOrShiftingJourney()) {
        // if (currentProductType === 'INTERNET') {
        if (currentProductType === 'INTERNET' || currentProductType === 'DEDICATED_INTERNET') {
          return 'ILL';
        }
        if (currentProductType === 'MPLS') {
          return 'VPN';
        }
        return currentProductType;
      }

      // for Customer User
      if (currentProductType === 'DEDICATED_INTERNET') {
        return 'ILL';
      }

      if (isAOIOrder()) {
        currentProductType = 'Airtel Office Internet';
      }

      return currentProductType;
    };

    if (isAOIOrder()) {
      currentOrderType = 'New';
    }
    const accountNameForAOI = getCurrentLead()?.customer?.authorizedSignatory?.companyName;

    // const fetchOrderFn = async () => {
    //   fetchOrder().
    //   then(
    //     (res) =>{
    //       const result = res.result
    //     },
    //     (err) => {
    //       console.log(err);
    //     }
    //   )
    // }

    // useEffect(()=>{
    //   fetchOrderFn();
    // },[])

    return (
      <div className="auth-header-content createOrder">
        {isSingleUpgradeOrShiftingJourney() ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <span className="title_text_buy">{getTitleName()}</span>
            {singleUpgradeLeadId && (
              <p
                style={{
                  color: '#555A5A',
                  fontSize: 12
                }}
              >
                Request ID: {singleUpgradeLeadId}
              </p>
            )}
          </div>
        ) : (
          <>
            <span className="title_text_buy">{`Create ${currentOrderType ?? ''} Order - ${getProductName() ?? ''}`}</span>
            {/* <span>{accountNameForAOI ? `Account Name - ${accountNameForAOI ? accountNameForAOI : ''}` : ''} </span> */}
          </>
        )}
        {isAOITmoRedirected && (
          <div
            style={{
              marginLeft: '700px'
            }}
          >
            <Button value="Submit" onClick={trackMyOrderButtonHandler} />
          </div>
        )}
        {!isAOILandingPage() && (
          <div className="form_wrapper">
            <div className="form_section">
              {isAOIOrder() ? (
                <>
                  <span
                    style={{
                      fontWeight: 'bold'
                    }}
                  >
                    {accountNameForAOI ? 'Account Name -' : ''}{' '}
                  </span>
                  <span>{accountNameForAOI ? accountNameForAOI : ''} </span>
                </>
              ) : (
                <div>
                  <label>Account</label>
                  <input type="text" disabled placeholder={isAOIOrder() ? accountNameForAOI : getAccountName() ?? ''} />
                </div>
              )}
            </div>
            {!isAOIOrder() && (
              <div className="form_section">
                <label>Product</label>
                <input type="text" disabled placeholder={getProductName() ?? ''} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [getCurrentLead(), orderDetail]);

export const getFileUploadErrorTemplate = (errorTemplate: React.ReactNode) => (
  /**
   * div element added for notification layout
   */
  <div>{errorTemplate}</div>
);

export const getDoaDetailsNotification = (doaDetails: any) => (
  <p>
    {LEAD_STATUS_MSG.SENT_DOA_REQUEST_MSG}
    <span> {doaDetails?.name} </span> (
    <a className="assistance_link" href={'mailto: ' + doaDetails?.email}>
      {doaDetails?.email}
    </a>
    , <span>{doaDetails?.phoneNumber} )</span>
  </p>
);

export const getKamDetailsNotification = (kamDetails: any) => (
  <p>
    {CUSTOMER_MESSAGE.NEED_ASSISTANCE}
    <span> {kamDetails?.firstName} </span>(
    <a className="assistance_link" href={'mailto: ' + kamDetails?.email}>
      {kamDetails?.email}
    </a>{' '}
    ,{' '}
    <span>
      {kamDetails?.phoneNumber}) {CUSTOMER_MESSAGE.NEED_ASSISTANCE_CONTACT}
    </span>
  </p>
);
